/**
 * MIDI event encoders for WAM dispatch.
 *
 * WAMs receive note/controller events through `scheduleEvents([{ type:
 * 'wam-midi', data: { bytes } }])` where `bytes` is a 3-byte MIDI
 * message. This module centralises the status-byte + channel-mask
 * encoding so the effect and instrument adapters share one
 * implementation instead of sprinkling bitmath across call sites.
 *
 * Channel numbering: MIDI uses 1-16 in spec docs but 0-15 in wire
 * encoding. These helpers accept 0-15 (the encoding value). Callers
 * that hold human-facing channel numbers subtract one at their
 * boundary — cleaner than spreading the adjustment here.
 *
 * All values are clamped to their valid range (0-127 for data bytes,
 * 0-15 for channel) so callers can pass floats / out-of-range values
 * without triggering undefined-behaviour downstream. Velocity of 0 on
 * note-on is kept — the MIDI spec defines velocity-0 note-on as a
 * silent note-off, and some WAMs rely on that alias for voice
 * allocation.
 */

export type MidiStatus = 'note-on' | 'note-off' | 'cc' | 'pitch-bend'

const NOTE_ON_STATUS = 0x90
const NOTE_OFF_STATUS = 0x80
const CC_STATUS = 0xb0
const PITCH_BEND_STATUS = 0xe0

function clampByte(value: number): number {
  return Math.max(0, Math.min(127, Math.round(value)))
}

function clampChannel(channel: number): number {
  return Math.max(0, Math.min(15, Math.round(channel))) & 0x0f
}

/**
 * Build a note-on message. Velocity is expected as 0-127. If callers
 * hold a 0-1 float, multiply by 127 before passing. Default channel
 * is 0 — most monotimbral WAMs ignore the channel bits anyway.
 */
export function noteOnBytes(
  note: number,
  velocity: number,
  channel = 0,
): [number, number, number] {
  return [
    NOTE_ON_STATUS | clampChannel(channel),
    clampByte(note),
    clampByte(velocity),
  ]
}

/**
 * Build a note-off message. Release-velocity defaults to 64 (the MIDI
 * spec's "no opinion" value) so WAMs that don't honour release
 * velocity get a neutral signal.
 */
export function noteOffBytes(
  note: number,
  releaseVelocity = 64,
  channel = 0,
): [number, number, number] {
  return [
    NOTE_OFF_STATUS | clampChannel(channel),
    clampByte(note),
    clampByte(releaseVelocity),
  ]
}

/**
 * Build a Control Change (CC) message. Controller ids 0-127; value
 * 0-127. For 14-bit CCs, callers must send an MSB + LSB pair as two
 * separate CC messages — we don't special-case that here.
 */
export function ccBytes(
  controller: number,
  value: number,
  channel = 0,
): [number, number, number] {
  return [
    CC_STATUS | clampChannel(channel),
    clampByte(controller),
    clampByte(value),
  ]
}

/**
 * Build a 14-bit pitch-bend message. `value` is -1..+1 where -1 is
 * full-bend-down and +1 is full-bend-up; 0 is center. Translates to
 * a 14-bit unsigned int (0-16383, center 8192) and splits into LSB +
 * MSB bytes.
 */
export function pitchBendBytes(
  value: number,
  channel = 0,
): [number, number, number] {
  const clamped = Math.max(-1, Math.min(1, value))
  const int14 = Math.round((clamped + 1) * 8191.5)
  const lsb = int14 & 0x7f
  const msb = (int14 >> 7) & 0x7f
  return [PITCH_BEND_STATUS | clampChannel(channel), lsb, msb]
}

/**
 * Convenience — convert a MIDI note number to Hz. Reused across the
 * adapters that need to derive frequency for WAMs that expose a
 * direct-frequency param instead of accepting MIDI events.
 */
export function midiNoteToHz(note: number): number {
  return 440 * Math.pow(2, (note - 69) / 12)
}
